import { useState, useEffect } from "react";
import axios from "axios";
import { LinkSimple, ShieldWarning, FileText, CaretRight, CaretDown, Sparkle } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VERDICT = {
  pass: { label: "✅ PASS", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  review: { label: "⚠ REVIEW", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  fail: { label: "❌ FAIL", cls: "bg-rose-100 text-rose-700 border-rose-200" },
  pending: { label: "• NOT REVIEWED", cls: "bg-zinc-100 text-zinc-400 border-zinc-200" },
};
const SCVERDICT = {
  pass: "bg-emerald-500 text-white", review: "bg-amber-500 text-white",
  fail: "bg-rose-500 text-white", pending: "bg-zinc-600 text-zinc-300",
};
const REC_BADGE = { build: "bg-emerald-600 text-white", watch: "bg-amber-500 text-white", reject: "bg-rose-600 text-white", pending: "bg-zinc-500 text-white" };
const REC_LABEL = { build: "BUILD", watch: "WATCH", reject: "REJECT", pending: "NEEDS REVIEW" };
const ANNO_TAGS = [["useful", "Useful"], ["real_pain", "Real pain"], ["strong", "Strong evidence"], ["off_topic", "Off topic"]];

const SCORE_DIMS = [["Pain", "customer_pain"], ["Demand", "search_demand"], ["Commercial", "commercial"],
  ["Competition", "competition"], ["Manufacturing", "manufacturing"], ["Economics", "economics"],
  ["Advertising", "advertising"], ["Regulation", "regulatory"]];

// Mirror of the backend verdict logic so the scorecard updates live as boxes are ticked.
const computeVerdict = (sec, state) => {
  const items = sec?.checklist || [];
  if (!items.length) return "pending";
  const st = state || {};
  if (items.some((i) => i.negative && st[i.id])) return "fail";
  const pos = items.filter((i) => !i.negative);
  if (sec.single) return pos.some((i) => st[i.id]) ? "pass" : "pending";
  if (!items.some((i) => st[i.id])) return "pending";
  const checked = pos.filter((i) => st[i.id]).length;
  if (checked === pos.length) return "pass";
  return checked > 0 ? "review" : "pending";
};

const computeRec = (rows) => {
  const c = { pass: 0, review: 0, fail: 0, pending: 0 };
  rows.forEach((r) => (c[r.verdict] += 1));
  const painFail = rows.some((r) => r.verdict === "fail" && (r.key === "customer_pain" || r.key === "search_demand"));
  if (c.fail === 0 && c.review === 0 && c.pending === 0) return "build";
  if (c.fail >= 3 || painFail) return "reject";
  if (c.pass === 0 && c.review === 0) return "pending";
  return "watch";
};

const Links = ({ links }) =>
  links?.length ? (
    <div className="space-y-1.5 mt-2">
      {links.map((l, i) => (
        <div key={i}>
          <a href={l.url} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-violet-600 hover:underline bg-violet-50 px-2.5 py-1 rounded-full"
            data-testid="dd-evidence-link">
            <LinkSimple size={11} /> {l.label}
          </a>
          {l.question && <div className="text-[10px] text-zinc-400 mt-0.5 ml-1">↳ {l.question}</div>}
        </div>
      ))}
    </div>
  ) : null;

const AiReason = ({ reason }) => {
  const [open, setOpen] = useState(false);
  if (!reason) return null;
  return (
    <div className="mt-2">
      <button onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 text-[10px] font-bold text-violet-600 hover:underline"
        data-testid="dd-ai-reason-toggle">
        <Sparkle size={11} weight="fill" /> Why did the AI pass this? {open ? <CaretDown size={9} /> : <CaretRight size={9} />}
      </button>
      {open && (
        <div className="mt-1.5 rounded-lg bg-zinc-50 border border-zinc-100 px-3 py-2" data-testid="dd-ai-reason">
          <div className="text-[10px] uppercase tracking-wide text-zinc-400 font-bold mb-1">Reason</div>
          {reason.lines.map((l, i) => <div key={i} className="text-[11px] text-zinc-600">• {l}</div>)}
          {reason.evidence_strength != null && (
            <div className="mt-1.5 text-[11px]"><span className="text-zinc-400">Evidence Strength</span> <b className="text-violet-700">{reason.evidence_strength}</b></div>
          )}
        </div>
      )}
    </div>
  );
};

const Checklist = ({ sec, secKey, state, onToggle }) => (
  <div className="mb-1">
    {sec.objective && <div className="text-[11px] text-zinc-600 mb-2"><b className="text-violet-700">Objective:</b> {sec.objective}</div>}
    {sec.question && <div className="text-[11px] font-bold text-zinc-700 mb-1">{sec.question}</div>}
    <div className="text-[10px] uppercase tracking-wide text-zinc-400 font-bold mb-1">What to verify</div>
    <div className="space-y-0.5">
      {(sec.checklist || []).map((item) => {
        const on = !!(state && state[item.id]);
        return (
          <button key={item.id} onClick={() => onToggle(secKey, item.id, sec.single)}
            className={`w-full flex items-start gap-2 text-left text-[11px] px-2 py-1 rounded-lg transition-colors ${on ? "bg-violet-50" : "hover:bg-zinc-50"}`}
            data-testid={`dd-check-${secKey}-${item.id}`}>
            <span className={`mt-[1px] w-3.5 h-3.5 rounded border flex-shrink-0 flex items-center justify-center text-[9px] leading-none ${on ? (item.negative ? "bg-rose-500 border-rose-500 text-white" : "bg-violet-600 border-violet-600 text-white") : "border-zinc-300 text-transparent"}`}>✓</span>
            <span className={on ? "text-zinc-800 font-medium" : "text-zinc-500"}>{item.label}</span>
          </button>
        );
      })}
    </div>
  </div>
);

const Section = ({ n, title, sec, secKey, state, onToggle, verdict, children }) => (
  <div className="rounded-2xl bg-white/85 border border-violet-100 p-5" data-testid={`dd-section-${n}`}>
    <div className="flex items-center gap-2 mb-3">
      <span className="text-[10px] font-black text-violet-500 bg-violet-50 w-6 h-6 rounded-full flex items-center justify-center">{n}</span>
      <h4 className="text-sm font-bold text-zinc-900">{title}</h4>
      <span className={`ml-auto text-[9px] font-black uppercase px-2.5 py-1 rounded-full border ${VERDICT[verdict].cls}`} data-testid={`dd-verdict-${secKey}`}>
        {VERDICT[verdict].label}
      </span>
    </div>
    <Checklist sec={sec} secKey={secKey} state={state} onToggle={onToggle} />
    <AiReason reason={sec.ai_reason} />
    <div className="mt-3 border-t border-zinc-100 pt-3">
      {sec.status !== "collected"
        ? <div className="text-[11px] text-zinc-400 italic" data-testid="dd-not-enough">Not enough evidence collected.{sec.note ? ` ${sec.note}` : ""}</div>
        : children}
      <Links links={sec.evidence_links} />
    </div>
  </div>
);

const ConsensusRollup = ({ data, annos }) => {
  const urls = (data || []).flatMap((p) => (p.items || []).map((it) => it.url));
  const total = urls.length;
  const reviewed = urls.filter((u) => (annos[u]?.tags || []).length);
  const R = reviewed.length;
  const count = (tag) => reviewed.filter((u) => (annos[u].tags || []).includes(tag)).length;
  const realPain = count("real_pain"), useful = count("useful"), strong = count("strong"), off = count("off_topic");
  let consensus = null;
  if (R) {
    const per = reviewed.map((u) => {
      const t = annos[u].tags || [];
      if (t.includes("off_topic")) return 0;
      return (t.includes("real_pain") ? 0.4 : 0) + (t.includes("strong") ? 0.4 : 0) + (t.includes("useful") ? 0.2 : 0);
    });
    consensus = Math.round((per.reduce((a, b) => a + b, 0) / R) * 100) / 10;
  }
  return (
    <div className="rounded-xl bg-violet-50/60 border border-violet-100 p-3 mb-3" data-testid="dd-consensus">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-bold text-zinc-700">Human Consensus</span>
        <span className="text-[10px] text-zinc-400">You reviewed {R} / {total}</span>
      </div>
      {R === 0 ? (
        <div className="text-[11px] text-zinc-400 italic">Not reviewed yet — tag the discussions below to build a consensus score.</div>
      ) : (
        <>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] mb-2">
            <span className="text-emerald-700">✓ Real Pain <b>{realPain}</b></span>
            <span className="text-emerald-700">✓ Useful <b>{useful}</b></span>
            <span className="text-emerald-700">✓ Strong Evidence <b>{strong}</b></span>
            <span className="text-rose-600">✗ Off Topic <b>{off}</b></span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-black text-violet-700" data-testid="dd-consensus-score">{consensus}</span>
            <span className="text-[10px] text-zinc-400">/ 10 human consensus</span>
          </div>
        </>
      )}
    </div>
  );
};

const PainPlatform = ({ p, annos, annotate }) => {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? p.items : p.items.slice(0, 5);
  return (
    <div className="mb-3" data-testid="dd-pain-platform">
      <div className="text-xs font-bold text-zinc-700 mb-1.5">
        {p.platform} · {p.count} discussions
        <span className="text-[10px] font-normal text-zinc-400"> — open &amp; tag each as you review</span>
      </div>
      <div className="space-y-2">
        {shown.map((it, j) => {
          const tags = annos[it.url]?.tags || [];
          return (
            <div key={j} className="rounded-lg border border-zinc-100 px-2 py-1.5" data-testid={`dd-pain-item-${j}`}>
              <a href={it.url} target="_blank" rel="noreferrer"
                className="flex items-start gap-1.5 text-[11px] text-zinc-500 italic hover:text-violet-600"
                data-testid={`dd-pain-link-${j}`}>
                <LinkSimple size={11} className="text-violet-400 mt-0.5 flex-shrink-0" />
                <span>"{it.text?.slice(0, 140)}"</span>
              </a>
              <div className="flex flex-wrap gap-1 mt-1.5">
                {ANNO_TAGS.map(([id, label]) => {
                  const on = tags.includes(id);
                  return (
                    <button key={id} onClick={() => annotate(it.url, it.text?.slice(0, 200), id)}
                      className={`text-[9px] font-bold px-2 py-0.5 rounded-full transition-all ${on ? (id === "off_topic" ? "bg-rose-500 text-white" : "bg-emerald-600 text-white") : "bg-zinc-100 text-zinc-500 hover:bg-zinc-200"}`}
                      data-testid={`dd-anno-${id}-${j}`}>
                      {on ? "✓ " : ""}{label}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      {p.items.length > 5 && (
        <button onClick={() => setExpanded((e) => !e)}
          className="text-[10px] font-bold text-violet-600 mt-1 ml-2 hover:underline"
          data-testid="dd-pain-showmore">
          {expanded ? "Show fewer" : `Show all ${p.items.length} links`}
        </button>
      )}
    </div>
  );
};

const Cite = ({ evidence }) =>
  (evidence || []).map((ev, j) =>
    ev.url ? (
      <a key={j} href={ev.url} target="_blank" rel="noreferrer"
        className="ml-1 text-[9px] font-bold text-violet-500 hover:underline" title={ev.label}
        data-testid={`bp-cite-${ev.id}`}>[{ev.id}]</a>
    ) : (
      <span key={j} className="ml-1 text-[9px] font-bold text-zinc-400" title={ev.label}>[{ev.id}]</span>
    ));

const BpLabel = ({ children }) => (
  <div className="text-[10px] font-bold uppercase text-zinc-400 mb-1 mt-2">{children}</div>
);

const PhaseTags = ({ phases }) =>
  (phases || []).length ? (
    <span className="ml-auto flex gap-1">
      {phases.map((p) => (
        <span key={p} className="text-[9px] font-black px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500">{p}</span>
      ))}
    </span>
  ) : null;

const BpCard = ({ title, section, testid, children }) => {
  if (!section) return null;
  if (section.status === "not_enough")
    return (
      <div className="rounded-xl border border-zinc-100 px-3 py-2" data-testid={testid}>
        <div className="flex items-center gap-2"><span className="text-[11px] font-bold text-zinc-800">{title}</span><PhaseTags phases={section.source_phases} /></div>
        <div className="text-[11px] text-zinc-400 italic mt-1">{section.note || "Not enough evidence collected."}</div>
      </div>
    );
  return (
    <div className="rounded-xl border border-zinc-100 px-3 py-2.5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1"><span className="text-[11px] font-bold text-zinc-800">{title}</span><PhaseTags phases={section.source_phases} /></div>
      {children}
    </div>
  );
};

const RoadmapWeek = ({ label, tasks }) =>
  (tasks || []).length ? (
    <div className="mb-1.5">
      <span className="text-[10px] font-black text-violet-600">{label}</span>
      {tasks.map((t, i) => (
        <div key={i} className="text-[11px] text-zinc-600 ml-2">• {t.task}<Cite evidence={t.evidence} /></div>
      ))}
    </div>
  ) : null;

const Report = ({ cid }) => {
  const [d, setD] = useState(null);
  const [checklist, setChecklist] = useState({});
  const [annos, setAnnos] = useState({});
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setD(null); setChecklist({}); setAnnos({}); setSaved(false);
    axios.get(`${API}/commerce/diligence/${cid}`).then(({ data }) => {
      setD(data);
      setChecklist(data.founder_checklist || {});
      setAnnos(data.link_annotations || {});
    }).catch(() => setD(false));
  }, [cid]);

  const onToggle = (secKey, itemId, single) => {
    setSaved(false);
    setChecklist((c) => {
      const sec = { ...(c[secKey] || {}) };
      if (single) Object.keys(sec).forEach((k) => { if (k !== itemId) sec[k] = false; });
      sec[itemId] = !sec[itemId];
      return { ...c, [secKey]: sec };
    });
  };

  const annotate = async (url, quote, tag) => {
    const cur = annos[url]?.tags || [];
    const tags = cur.includes(tag) ? cur.filter((t) => t !== tag) : [...cur, tag];
    setAnnos((a) => ({ ...a, [url]: { ...(a[url] || {}), tags, quote } }));
    try { await axios.post(`${API}/commerce/diligence/annotate`, { cluster_id: cid, url, tags, quote }); } catch (e) { /* noop */ }
  };

  if (d === null) return <div className="text-center py-10 text-zinc-400" data-testid="dd-loading">Loading due diligence…</div>;
  if (d === false) return <div className="text-center py-10 text-rose-400">Couldn't load report.</div>;

  const s = d.sections;
  const rows = SCORE_DIMS.map(([label, key]) => ({ dimension: label, key, verdict: computeVerdict(s[key], checklist[key]) }));
  const rec = computeRec(rows);
  const memo = d.investment_memo;
  const secProps = (key) => ({ secKey: key, state: checklist[key], onToggle, verdict: computeVerdict(s[key], checklist[key]) });

  const save = async () => {
    try { await axios.post(`${API}/commerce/diligence/save`, { cluster_id: cid, checklist }); setSaved(true); } catch (e) { /* noop */ }
  };

  return (
    <div className="space-y-4" data-testid="dd-report">
      <div className="text-lg font-bold text-zinc-900">{d.label} <span className="text-xs text-violet-500 font-semibold">· {d.market} · ES {d.evidence_strength}</span></div>
      {d.build_confidence && (
        <div className="rounded-2xl border border-violet-200 bg-violet-50/50 px-5 py-4 flex items-center gap-5 flex-wrap" data-testid="dd-build-confidence">
          <div className="flex items-baseline gap-2">
            <span className="text-4xl font-black text-violet-700" data-testid="dd-build-confidence-score">{d.build_confidence.score}</span>
            <span className="text-[10px] text-zinc-500 font-bold uppercase">/ 100 Build Confidence</span>
          </div>
          <div className="text-[10px] text-zinc-500">"Ignoring marketing, is this worth building?" — derived, never replaces the underlying scores</div>
          <div className="flex gap-2 ml-auto flex-wrap">
            {Object.entries(d.build_confidence.components).map(([k, v]) => (
              <span key={k} className="text-[10px] px-2 py-1 rounded-full bg-white border border-violet-100 text-zinc-600">
                {k.replace(/_/g, " ").replace("confidence", "").trim()}: <b className="text-violet-700">{v}</b>
              </span>
            ))}
          </div>
        </div>
      )}

      <Section n={1} title="Customer Pain Validation" sec={s.customer_pain} {...secProps("customer_pain")}>
        <ConsensusRollup data={s.customer_pain.data} annos={annos} />
        {s.customer_pain.data?.map?.((p, i) => (
          <PainPlatform key={i} p={p} annos={annos} annotate={annotate} />
        ))}
      </Section>

      <Section n={2} title="Search Demand Validation" sec={s.search_demand} {...secProps("search_demand")}>
        <div className="text-[11px] text-zinc-600 flex flex-wrap gap-x-4 gap-y-1">
          <span>Keywords: <b>{(s.search_demand.data.keywords || []).slice(0, 5).join(", ")}</b></span>
          {s.search_demand.data.google_trends_score != null && <span>Trends: <b>{s.search_demand.data.google_trends_score}</b></span>}
          {s.search_demand.data.validation_status && <span>Status: <b>{s.search_demand.data.validation_status}</b></span>}
        </div>
      </Section>

      <Section n={3} title="Existing Solutions — Who Already Sells This?" sec={s.commercial} {...secProps("commercial")}>
        {s.commercial.data.summary && (
          <div className="text-[11px] text-zinc-600 space-y-1">
            <div className="text-zinc-700">{s.commercial.data.summary.sentence}</div>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              <span>Products: <b>{s.commercial.data.summary.total_products}</b></span>
              <span>Avg price: <b>{s.commercial.data.summary.avg_price != null ? `$${s.commercial.data.summary.avg_price}` : "—"}</b></span>
              <span>Avg rating: <b>{s.commercial.data.summary.avg_rating != null ? `${s.commercial.data.summary.avg_rating}★` : "—"}</b></span>
              <span>Avg reviews: <b>{s.commercial.data.summary.avg_review_count ?? "—"}</b></span>
            </div>
            {s.commercial.data.summary.top_brands?.length > 0 && (
              <div>Top brands: <b>{s.commercial.data.summary.top_brands.join(", ")}</b></div>
            )}
          </div>
        )}
      </Section>

      <Section n={4} title="Competition Validation" sec={s.competition} {...secProps("competition")}>
        {Array.isArray(s.competition.data) && (
          <div className="space-y-1">
            {s.competition.data.slice(0, 10).map((c, i) => (
              <div key={i} className="text-[11px] text-zinc-600 flex justify-between"><span>{c.brand || c.name}</span><span>{c.price} · ★{c.rating} · {c.reviews} rev</span></div>
            ))}
          </div>
        )}
      </Section>

      <Section n={5} title="Customer Dissatisfaction — Review Intelligence" sec={s.dissatisfaction} {...secProps("dissatisfaction")}>
        {(s.dissatisfaction.data?.themes || []).length > 0 && (
          <div className="space-y-2" data-testid="dd-review-intel">
            {s.dissatisfaction.data.themes.slice(0, 10).map((t, i) => (
              <div key={i} className="rounded-lg border border-zinc-100 px-3 py-2" data-testid={`dd-theme-${i}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] font-bold text-zinc-800">{t.theme}</span>
                  <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-600 border border-rose-100">{(t.type || "complaint").replace("_", " ")}</span>
                  <span className="ml-auto text-[10px] text-zinc-400">{t.frequency} reviews · conf {t.confidence}</span>
                </div>
                {t.description && <div className="text-[11px] text-zinc-500 mt-0.5">{t.description}</div>}
                {(t.example_quotes || []).slice(0, 2).map((q, j) => (
                  <a key={j} href={q.url} target="_blank" rel="noreferrer"
                    className="block text-[10px] text-zinc-400 italic mt-1 hover:text-violet-600" data-testid={`dd-theme-${i}-quote-${j}`}>
                    "{q.quote}" — {q.rating}★
                  </a>
                ))}
              </div>
            ))}
            {(s.dissatisfaction.data.unmet_needs || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1" data-testid="dd-unmet-needs">
                <span className="text-[10px] font-bold text-zinc-500 mr-1">Unmet needs:</span>
                {s.dissatisfaction.data.unmet_needs.map((u, i) => (
                  <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-100">{u}</span>
                ))}
              </div>
            )}
          </div>
        )}
      </Section>
      <Section n={6} title="Gap Analysis — What Should Be Built?" sec={s.gap} {...secProps("gap")}>
        {s.gap.status === "collected" && (
          <div className="space-y-3" data-testid="dd-gap">
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-black text-violet-700" data-testid="dd-gap-confidence">{s.gap.data.gap_confidence}</span>
              <span className="text-[10px] text-zinc-400">/ 100 gap confidence (deterministic — CV · Review Intel · Evidence Strength · Decision Confidence · Sources)</span>
            </div>
            {s.gap.data.opportunity_statement && (
              <p className="text-[12px] text-zinc-700 rounded-xl bg-violet-50/60 border border-violet-100 px-3 py-2" data-testid="dd-gap-statement">{s.gap.data.opportunity_statement}</p>
            )}
            {s.gap.data.product_concept?.name && (
              <div className="rounded-xl border border-zinc-200 bg-white px-3 py-2.5" data-testid="dd-gap-concept">
                <div className="text-[12px] font-bold text-zinc-900">{s.gap.data.product_concept.name}</div>
                <div className="text-[11px] text-zinc-600 mt-0.5">{s.gap.data.product_concept.one_sentence}</div>
                <div className="text-[10px] text-zinc-500 mt-1.5"><b>Target:</b> {s.gap.data.product_concept.target_customer} · <b>Core problem:</b> {s.gap.data.product_concept.core_problem}</div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {(s.gap.data.product_concept.must_have_features || []).map((f, i) => (
                    <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">✓ {f}</span>
                  ))}
                  {(s.gap.data.product_concept.avoid_features || []).map((f, i) => (
                    <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-rose-50 text-rose-600 border border-rose-100">✗ {f}</span>
                  ))}
                </div>
                {s.gap.data.product_concept.differentiation && (
                  <div className="text-[10px] text-zinc-500 mt-1.5"><b>Differentiation:</b> {s.gap.data.product_concept.differentiation}</div>
                )}
                {s.gap.data.product_concept.evidence_summary && (
                  <div className="text-[10px] text-violet-600 mt-1">{s.gap.data.product_concept.evidence_summary}</div>
                )}
              </div>
            )}
            {[["Top unmet problems", "top_unmet_problems"], ["Missing features", "missing_features"],
              ["Repeatedly requested", "requested_features"], ["Competitors do badly", "competitor_weaknesses"],
              ["Competitors ignore", "competitor_blind_spots"], ["Customer workarounds", "customer_workarounds"]]
              .filter(([, k]) => (s.gap.data[k] || []).length > 0)
              .map(([label, k]) => (
                <div key={k} data-testid={`dd-gap-${k}`}>
                  <div className="text-[10px] font-bold uppercase text-zinc-400 mb-1">{label}</div>
                  {s.gap.data[k].map((it, i) => (
                    <div key={i} className="text-[11px] text-zinc-600 mb-1">
                      • {it.text}
                      {(it.evidence || []).map((ev, j) => (
                        <a key={j} href={ev.url} target="_blank" rel="noreferrer"
                          className="ml-1 text-[9px] font-bold text-violet-500 hover:underline" title={ev.label}
                          data-testid={`dd-gap-ev-${k}-${i}-${j}`}>[{ev.id}]</a>
                      ))}
                    </div>
                  ))}
                </div>
              ))}
          </div>
        )}
      </Section>
      <Section n={7} title="Manufacturing Validation" sec={s.manufacturing} {...secProps("manufacturing")}>
        {s.manufacturing.status === "collected" && s.manufacturing.data?.suppliers && (
          <div className="space-y-2" data-testid="dd-manufacturers">
            <div className="flex items-center gap-2 flex-wrap">
              {s.manufacturing.data.manufacturing_risk && (
                <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full border ${
                  s.manufacturing.data.manufacturing_risk.level === "High" ? "bg-rose-50 text-rose-600 border-rose-100" :
                  s.manufacturing.data.manufacturing_risk.level === "Medium" ? "bg-amber-50 text-amber-600 border-amber-100" :
                  "bg-emerald-50 text-emerald-700 border-emerald-100"}`} data-testid="dd-mfg-risk">
                  Manufacturing risk: {s.manufacturing.data.manufacturing_risk.level}
                </span>
              )}
              <span className="text-[10px] text-zinc-400">{s.manufacturing.data.summary?.sentence}</span>
            </div>
            {(s.manufacturing.data.manufacturing_risk?.factors || []).map((f, i) => (
              <div key={i} className="text-[10px] text-amber-700">⚠ {f}</div>
            ))}
            {s.manufacturing.data.suppliers.slice(0, 6).map((sp, i) => (
              <div key={i} className="rounded-lg border border-zinc-100 px-3 py-2" data-testid={`dd-supplier-${i}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] font-bold text-zinc-800">{sp.supplier_name}</span>
                  {sp.verified_supplier && <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-600 border border-sky-100">verified</span>}
                  {sp.export_history && <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">US export ✓</span>}
                  {sp.private_label && <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-600 border border-violet-100">private label*</span>}
                  <span className="ml-auto text-sm font-black text-violet-700" data-testid={`dd-supplier-${i}-confidence`}>{sp.manufacturer_confidence}</span>
                  <span className="text-[9px] text-zinc-400">/100</span>
                </div>
                <div className="text-[10px] text-zinc-500 mt-0.5">
                  MOQ {sp.moq_min ?? "?"}–{sp.moq_max ?? "?"} · {sp.years_gold_supplier}y gold · {sp.matching_listings} matching listings
                  {sp.avg_review_score ? ` · ${sp.avg_review_score}★` : ""}
                  {sp.export_history ? ` · ${sp.export_history.shipments_12m ?? 0} US shipments/12m` : ""}
                  {sp.location ? ` · ${sp.location}` : ""}
                </div>
                {sp.confidence_components && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {Object.entries(sp.confidence_components).map(([k, v]) => (
                      <span key={k} className="text-[9px] px-1.5 py-0.5 rounded bg-zinc-50 text-zinc-500 border border-zinc-100">{k.replace(/_/g, " ")}: {v}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Section>
      <Section n={8} title="Economics Validation" sec={s.economics} {...secProps("economics")}>
        {s.economics.status === "collected" && s.economics.data?.line_items && (
          <div className="space-y-2" data-testid="dd-economics">
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="text-3xl font-black text-violet-700" data-testid="dd-econ-confidence">{s.economics.data.economics_confidence}</span>
              <span className="text-[10px] text-zinc-400">/ 100 economics confidence (deterministic)</span>
              {s.economics.data.markup_multiple_mid && (
                <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-2 py-0.5">~{s.economics.data.markup_multiple_mid}x markup</span>
              )}
            </div>
            <p className="text-[11px] text-zinc-600" data-testid="dd-econ-sentence">{s.economics.data.summary?.sentence}</p>
            <div className="rounded-xl border border-zinc-100 overflow-hidden">
              {s.economics.data.line_items.map((l, i) => (
                <div key={i} className={`flex items-center gap-2 px-3 py-1.5 text-[11px] ${i % 2 ? "bg-zinc-50/50" : ""}`} data-testid={`dd-econ-line-${l.name}`}>
                  <span className="font-semibold text-zinc-700 w-44 shrink-0">{l.name.replace(/_/g, " ")}</span>
                  <span className="text-zinc-900 font-bold">{l.low === l.high ? l.low : `${l.low} – ${l.high}`} <span className="text-[9px] font-normal text-zinc-400">{l.unit}</span></span>
                  <span className={`ml-auto text-[9px] px-1.5 py-0.5 rounded-full border ${
                    l.basis.startsWith("ASSUMPTION") ? "bg-amber-50 text-amber-700 border-amber-100" :
                    l.basis.startsWith("DATA") ? "bg-emerald-50 text-emerald-700 border-emerald-100" :
                    "bg-zinc-50 text-zinc-500 border-zinc-100"}`} title={l.basis}>
                    {l.basis.split("(")[0]}
                  </span>
                </div>
              ))}
            </div>
            {s.economics.data.confidence_components && (
              <div className="flex flex-wrap gap-1">
                {Object.entries(s.economics.data.confidence_components).map(([k, v]) => (
                  <span key={k} className="text-[9px] px-1.5 py-0.5 rounded bg-zinc-50 text-zinc-500 border border-zinc-100">{k.replace(/_/g, " ")}: {v}</span>
                ))}
              </div>
            )}
          </div>
        )}
      </Section>
      <Section n={9} title="Advertising Validation" sec={s.advertising} {...secProps("advertising")}>
        {s.advertising.status === "collected" && s.advertising.data?.advertising_confidence != null && (
          <div className="space-y-2" data-testid="dd-ads">
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="text-3xl font-black text-violet-700" data-testid="dd-ads-confidence">{s.advertising.data.advertising_confidence}</span>
              <span className="text-[10px] text-zinc-400">/ 100 advertising confidence (deterministic — observed ads only)</span>
            </div>
            <p className="text-[11px] text-zinc-600">{s.advertising.data.summary?.sentence}</p>
            {(s.advertising.data.common_ctas || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="dd-ads-ctas">
                <span className="text-[10px] font-bold text-zinc-500">Common CTAs:</span>
                {s.advertising.data.common_ctas.slice(0, 6).map((c, i) => (
                  <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-100">{c.cta} ×{c.count}</span>
                ))}
              </div>
            )}
            {[["Winning hooks", "repeated_hooks"], ["Repeated messaging", "repeated_messaging"],
              ["Offer patterns", "offer_patterns"], ["Compliance warnings", "compliance_warnings"]]
              .filter(([, k]) => ((s.advertising.data.patterns || {})[k] || []).length > 0)
              .map(([label, k]) => (
                <div key={k} data-testid={`dd-ads-${k}`}>
                  <div className={`text-[10px] font-bold uppercase mb-1 ${k === "compliance_warnings" ? "text-rose-500" : "text-zinc-400"}`}>{label}</div>
                  {s.advertising.data.patterns[k].map((p, i) => (
                    <div key={i} className="text-[11px] text-zinc-600 mb-1">
                      • {p.pattern} <span className="text-[9px] text-zinc-400">({p.frequency} ads)</span>
                      {(p.ads || []).slice(0, 3).map((a, j) => (
                        a.url ? <a key={j} href={a.url} target="_blank" rel="noreferrer" className="ml-1 text-[9px] font-bold text-violet-500 hover:underline" title={a.advertiser}>[{a.id}]</a> : null
                      ))}
                    </div>
                  ))}
                </div>
              ))}
          </div>
        )}
      </Section>

      <Section n={10} title="Regulatory Validation" sec={s.regulatory} {...secProps("regulatory")}>
        <div className="flex items-center gap-2 mb-1">
          <ShieldWarning size={16} className={s.regulatory.data.overall_risk === "high" ? "text-rose-500" : s.regulatory.data.overall_risk === "moderate" ? "text-amber-500" : "text-emerald-500"} weight="fill" />
          <span className="text-xs font-bold uppercase">{s.regulatory.data.overall_risk} risk</span>
        </div>
        {(s.regulatory.data.flags || []).map((f, i) => (
          <div key={i} className="text-[11px] text-zinc-500">{f.body} ({f.risk}): {f.explanation}</div>
        ))}
        <div className="text-[10px] text-zinc-400 mt-1">{s.regulatory.data.disclaimer}</div>
      </Section>

      <Section n={11} title="Business Blueprint — Exactly What To Build" sec={s.blueprint} {...secProps("blueprint")}>
        {s.blueprint?.status === "collected" && (() => {
          const d = s.blueprint.data;
          const pb = d.product_blueprint || {};
          const mb = d.manufacturing_blueprint || {};
          const pr = d.pricing_blueprint || {};
          const mp = d.market_positioning || {};
          const bd = d.brand_direction || {};
          const ob = d.offer_blueprint || {};
          const lr = d.launch_roadmap || {};
          const rk = d.risks || {};
          const mi = d.missing_information || [];
          return (
            <div className="space-y-2.5" data-testid="dd-blueprint">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-3xl font-black text-violet-700" data-testid="dd-bp-completeness">{d.blueprint_completeness?.score}</span>
                <span className="text-[10px] text-zinc-400">/ 100 blueprint completeness (deterministic — phases present / 6)</span>
              </div>

              <BpCard title="1 · Product Blueprint" section={pb} testid="dd-bp-product">
                <div className="text-[12px] font-bold text-zinc-900">{pb.working_name} <span className="text-[9px] font-normal text-zinc-400">(working name)</span></div>
                <div className="text-[11px] text-zinc-600 mt-0.5">{pb.one_sentence_positioning}</div>
                <div className="text-[10px] text-zinc-500 mt-1"><b>Core promise:</b> {pb.core_promise}</div>
                <div className="text-[10px] text-zinc-500 mt-0.5"><b>Target customer:</b> {pb.target_customer}</div>
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {(pb.must_have_features || []).map((f, i) => (
                    <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">✓ {f}</span>
                  ))}
                </div>
                {(pb.nice_to_have_features || []).length > 0 && (
                  <>
                    <BpLabel>Nice to have (customer-requested, P7)</BpLabel>
                    {pb.nice_to_have_features.map((f, i) => <div key={i} className="text-[10px] text-zinc-500">○ {f}</div>)}
                  </>
                )}
                {(pb.what_not_to_build || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {pb.what_not_to_build.map((f, i) => (
                      <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-rose-50 text-rose-600 border border-rose-100">✗ Do not build: {f}</span>
                    ))}
                  </div>
                )}
              </BpCard>

              <BpCard title="2 · Manufacturing Blueprint" section={mb} testid="dd-bp-manufacturing">
                {(mb.recommended_manufacturers || []).map((m, i) => (
                  <div key={i} className="text-[11px] text-zinc-600 flex items-center gap-2 flex-wrap">
                    <b className="text-zinc-800">{m.supplier_name}</b>
                    <span className="text-[10px] text-zinc-400">conf {m.manufacturer_confidence}/100 · MOQ {m.moq_min ?? "?"}–{m.moq_max ?? "?"}{m.location ? ` · ${m.location}` : ""}</span>
                    {m.us_export_history && <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">US export ✓</span>}
                    {m.private_label && <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-600 border border-violet-100">private label{m.private_label_inferred ? "*" : ""}</span>}
                  </div>
                ))}
                <div className="text-[10px] text-zinc-500 mt-1"><b>Min MOQ overall:</b> {mb.min_moq_overall ?? "—"} · <b>Risk:</b> {mb.manufacturing_risk}</div>
                {(mb.manufacturing_notes || []).map((f, i) => <div key={i} className="text-[10px] text-amber-700">⚠ {f}</div>)}
                <div className="text-[10px] text-zinc-400 italic mt-1">{mb.formula_customization_notes}</div>
              </BpCard>

              <BpCard title="3 · Pricing Blueprint" section={pr} testid="dd-bp-pricing">
                <div className="flex flex-wrap gap-2 text-[11px]">
                  <span className="px-2 py-1 rounded-lg bg-zinc-50 border border-zinc-100">Entry <b>${pr.entry_price}</b></span>
                  <span className="px-2 py-1 rounded-lg bg-violet-50 border border-violet-100">Recommended <b className="text-violet-700">${pr.recommended_retail_price}</b></span>
                  <span className="px-2 py-1 rounded-lg bg-zinc-50 border border-zinc-100">Premium <b>${pr.premium_price}</b></span>
                </div>
                <div className="text-[10px] text-zinc-500 mt-1.5">
                  Margin <b>{pr.gross_margin_range_pct?.[0]}–{pr.gross_margin_range_pct?.[1]}%</b> (~{pr.markup_multiple_mid}x) ·
                  Break-even <b>{pr.break_even_units?.[0]}–{pr.break_even_units?.[1]} units</b> ·
                  Min investment <b>${pr.min_startup_investment?.[0]}–{pr.min_startup_investment?.[1]}</b>
                </div>
                <div className="text-[10px] text-zinc-400 italic mt-1">{pr.caveat}</div>
              </BpCard>

              <BpCard title="4 · Market Positioning" section={mp} testid="dd-bp-positioning">
                <div className="text-[11px] text-zinc-600"><b className="text-zinc-800">Everyone sells:</b> {mp.everyone_sells?.text}<Cite evidence={mp.everyone_sells?.evidence} /></div>
                <div className="text-[11px] text-zinc-600 mt-1"><b className="text-rose-600">Customers hate:</b> {mp.customers_hate?.text}<Cite evidence={mp.customers_hate?.evidence} /></div>
                <div className="text-[11px] text-zinc-600 mt-1"><b className="text-emerald-700">We will build:</b> {mp.we_will_build?.text}<Cite evidence={mp.we_will_build?.evidence} /></div>
                {mp.statement && <p className="text-[11px] text-zinc-700 rounded-lg bg-violet-50/60 border border-violet-100 px-3 py-2 mt-1.5" data-testid="dd-bp-positioning-statement">{mp.statement}</p>}
              </BpCard>

              <BpCard title="5 · Brand Direction" section={bd} testid="dd-bp-brand">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-black uppercase px-2.5 py-1 rounded-full bg-zinc-900 text-white" data-testid="dd-bp-brand-tier">{bd.tier}</span>
                  <span className="text-[11px] text-zinc-600"><b>Tone:</b> {bd.tone}</span>
                  {(bd.personality || []).map((p, i) => (
                    <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-100">{p}</span>
                  ))}
                </div>
                {bd.rationale?.text && <div className="text-[10px] text-zinc-500 mt-1">{bd.rationale.text}<Cite evidence={bd.rationale.evidence} /></div>}
              </BpCard>

              <BpCard title="6 · Offer Blueprint" section={ob} testid="dd-bp-offer">
                {(ob.offers || []).map((o, i) => (
                  <div key={i} className="text-[11px] text-zinc-600 mb-1">
                    • <b className="text-zinc-800">{o.offer}</b>
                    {o.rationale && <span className="text-[10px] text-zinc-400"> — {o.rationale}</span>}
                    <Cite evidence={o.evidence} />
                  </div>
                ))}
              </BpCard>

              <BpCard title="7 · Launch Roadmap (4 weeks)" section={lr} testid="dd-bp-roadmap">
                <RoadmapWeek label="Week 1" tasks={lr.week_1} />
                <RoadmapWeek label="Week 2" tasks={lr.week_2} />
                <RoadmapWeek label="Week 3" tasks={lr.week_3} />
                <RoadmapWeek label="Week 4" tasks={lr.week_4} />
              </BpCard>

              <BpCard title="8 · Risks" section={rk} testid="dd-bp-risks">
                {[["Regulatory", "regulatory"], ["Manufacturing", "manufacturing"], ["Competition", "competition"], ["Supply chain", "supply_chain"]]
                  .filter(([, k]) => (rk[k] || []).length > 0)
                  .map(([label, k]) => (
                    <div key={k} className="mb-1">
                      <span className="text-[10px] font-black uppercase text-rose-500">{label}</span>
                      {rk[k].map((r, i) => (
                        <div key={i} className="text-[11px] text-zinc-600 ml-2">• {r.risk}<Cite evidence={r.evidence} /></div>
                      ))}
                    </div>
                  ))}
              </BpCard>

              <div className="rounded-xl border border-amber-200 bg-amber-50/60 px-3 py-2.5" data-testid="dd-bp-missing">
                <div className="text-[11px] font-bold text-amber-800 mb-1">9 · Missing Information — what's still unknown ({mi.length})</div>
                {mi.map((m, i) => (
                  <div key={i} className="text-[11px] text-zinc-700 mb-0.5" data-testid={`dd-bp-missing-${i}`}>
                    ☐ <b>{m.item}</b> <span className="text-[10px] text-zinc-500">— {m.why}</span>
                    <span className="ml-1 text-[9px] font-black px-1.5 py-0.5 rounded bg-white text-zinc-500 border border-amber-100">{m.source_phase}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
      </Section>

      {/* Scorecard — auto-derived from the checklists above */}
      <div className="rounded-2xl bg-zinc-900 text-zinc-100 p-6" data-testid="dd-scorecard">
        <div className="text-violet-300 font-bold uppercase tracking-wide text-xs mb-4">Due Diligence Scorecard</div>
        <div className="grid sm:grid-cols-2 gap-2 mb-4">
          {rows.map((r) => (
            <div key={r.key} className="flex items-center justify-between bg-zinc-800/60 rounded-lg px-3 py-2" data-testid={`dd-score-${r.key}`}>
              <span className="text-[12px] text-zinc-300">{r.dimension}</span>
              <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${SCVERDICT[r.verdict]}`}>{VERDICT[r.verdict].label}</span>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-4 border-t border-zinc-700 pt-4">
          <div>
            <div className="text-[10px] uppercase text-zinc-500">Recommendation</div>
            <div className={`inline-block mt-1 text-sm font-black px-4 py-1.5 rounded-full ${REC_BADGE[rec]}`} data-testid="dd-recommendation">{REC_LABEL[rec]}</div>
          </div>
          <button onClick={save} className="ml-auto text-xs font-bold px-5 py-2 rounded-full bg-violet-600 text-white hover:bg-violet-700" data-testid="dd-save">Save review</button>
          {saved && <span className="text-[11px] text-emerald-400 font-bold" data-testid="dd-saved">✓ Saved</span>}
        </div>
      </div>

      {/* Investment Memo */}
      <div className="rounded-2xl bg-zinc-900 text-zinc-100 p-6" data-testid="dd-investment-memo">
        <div className="flex items-center gap-2 mb-3 text-violet-300 font-bold uppercase tracking-wide text-xs"><FileText size={14} weight="fill" /> Investment Memo</div>
        <p className="text-sm text-zinc-300 mb-4">{memo.executive_summary}</p>
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-[12px]">
          {["problem", "market", "customer_voice", "demand", "competition", "gap", "manufacturing", "economics", "advertising", "risks"].map((k) => (
            <div key={k} className="flex gap-2"><CaretRight size={12} className="text-violet-400 mt-1 flex-shrink-0" /><span><b className="text-zinc-400 capitalize">{k.replace("_", " ")}:</b> <span className="text-zinc-200">{memo[k]}</span></span></div>
          ))}
        </div>
      </div>

      {/* Phase 12 — Decision Report (final orchestration layer) */}
      <DecisionReport cid={cid} />
    </div>
  );
};

const P12_VERDICT = {
  build: { label: "BUILD", cls: "bg-emerald-600 text-white" },
  wait: { label: "WAIT", cls: "bg-amber-500 text-white" },
  pass: { label: "PASS (skip)", cls: "bg-rose-600 text-white" },
  insufficient: { label: "INSUFFICIENT EVIDENCE", cls: "bg-zinc-500 text-white" },
};

const P12Bar = ({ score }) => (
  <div className="flex-1 h-1.5 rounded-full bg-zinc-700 overflow-hidden">
    <div className={`h-full rounded-full ${score >= 70 ? "bg-emerald-400" : score >= 35 ? "bg-amber-400" : "bg-rose-400"}`}
      style={{ width: `${Math.min(100, score)}%` }} />
  </div>
);

const DecisionReport = ({ cid }) => {
  const [rep, setRep] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    setRep(null); setErr("");
    axios.get(`${API}/commerce/decision-report/${cid}`)
      .then(({ data }) => setRep(data?.exists === false ? null : data))
      .catch(() => setRep(null));
  }, [cid]);

  const generate = () => {
    setBusy(true); setErr("");
    axios.post(`${API}/commerce/decision-report/run`, { cluster_id: cid })
      .then(({ data }) => setRep(data.report || null))
      .catch((e) => setErr(e?.response?.data?.detail || "Generation failed — try again"))
      .finally(() => setBusy(false));
  };

  return (
    <div className="rounded-2xl bg-zinc-900 text-zinc-100 p-6" data-testid="dd-p12">
      <div className="flex items-center gap-2 mb-1 text-violet-300 font-bold uppercase tracking-wide text-xs">
        <Sparkle size={14} weight="fill" /> Phase 12 · Decision Report
      </div>
      <p className="text-[11px] text-zinc-500 mb-4">One decision-grade verdict combining every phase (P1-P11). Deterministic scores from cached phase data — the AI only writes the narrative, citing its sources.</p>

      {!rep && (
        <div className="flex items-center gap-3">
          <button onClick={generate} disabled={busy} data-testid="dd-p12-generate"
            className="text-xs font-bold px-5 py-2 rounded-full bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50">
            {busy ? "Combining all phases…" : "Generate Decision Report"}
          </button>
          {err && <span className="text-[11px] text-rose-400" data-testid="dd-p12-error">{err}</span>}
        </div>
      )}

      {rep && (
        <div className="space-y-4">
          <div className="flex items-center gap-4 flex-wrap">
            <span className={`text-sm font-black px-4 py-1.5 rounded-full ${P12_VERDICT[rep.verdict]?.cls || P12_VERDICT.insufficient.cls}`}
              data-testid="dd-p12-verdict">{P12_VERDICT[rep.verdict]?.label || rep.verdict}</span>
            {rep.overall_score != null && (
              <span data-testid="dd-p12-overall"><span className="text-3xl font-black text-violet-300">{rep.overall_score}</span>
                <span className="text-[10px] text-zinc-500"> / 100 overall</span></span>
            )}
            <span className="text-[10px] text-zinc-500" data-testid="dd-p12-completeness">
              Evidence: {rep.evidence_completeness?.present}/{rep.evidence_completeness?.total} phases
              {rep.evidence_completeness?.missing?.length ? ` · missing: ${rep.evidence_completeness.missing.join(", ")}` : ""}
            </span>
            <button onClick={generate} disabled={busy} data-testid="dd-p12-regenerate"
              className="ml-auto text-[10px] font-bold px-3 py-1 rounded-full border border-zinc-600 text-zinc-400 hover:text-white disabled:opacity-50">
              {busy ? "Regenerating…" : "↻ Regenerate"}
            </button>
          </div>

          <div className="grid sm:grid-cols-2 gap-2">
            {(rep.scorecard || []).map((r) => (
              <div key={r.key} className="bg-zinc-800/60 rounded-lg px-3 py-2" data-testid={`dd-p12-dim-${r.key}`} title={r.basis}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] text-zinc-300">{r.dimension} <span className="text-[9px] text-zinc-500">({r.source_phase})</span></span>
                  <span className="text-[11px] font-black text-zinc-100">{r.score != null ? r.score : "—"}</span>
                </div>
                {r.score != null ? <P12Bar score={r.score} /> : <div className="text-[9px] text-zinc-500">Not enough evidence collected.</div>}
              </div>
            ))}
          </div>

          {rep.executive_summary && rep.executive_summary !== "Not enough evidence collected." && (
            <p className="text-sm text-zinc-300 border-l-2 border-violet-500 pl-3" data-testid="dd-p12-summary">{rep.executive_summary}</p>
          )}

          <div className="grid sm:grid-cols-2 gap-4">
            {(rep.reasons_for || []).length > 0 && (
              <div data-testid="dd-p12-for">
                <div className="text-[10px] font-black uppercase text-emerald-400 mb-1">Why build</div>
                {rep.reasons_for.map((r, i) => (
                  <div key={i} className="text-[12px] text-zinc-300 mb-1.5">• {r.reason}
                    {r.phases.map((p) => <span key={p} className="ml-1 text-[9px] font-black px-1.5 py-0.5 rounded bg-zinc-800 text-violet-300 border border-zinc-700">{p}</span>)}
                  </div>
                ))}
              </div>
            )}
            {(rep.reasons_against || []).length > 0 && (
              <div data-testid="dd-p12-against">
                <div className="text-[10px] font-black uppercase text-rose-400 mb-1">Risks / why not</div>
                {rep.reasons_against.map((r, i) => (
                  <div key={i} className="text-[12px] text-zinc-300 mb-1.5">• {r.reason}
                    {r.phases.map((p) => <span key={p} className="ml-1 text-[9px] font-black px-1.5 py-0.5 rounded bg-zinc-800 text-violet-300 border border-zinc-700">{p}</span>)}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="text-[9px] text-zinc-600">Generated {rep.generated_at?.slice(0, 16).replace("T", " ")} UTC · {rep.model} · est ${rep.est_llm_cost_usd}</div>
        </div>
      )}
    </div>
  );
};

const FounderDiligence = ({ initialCid }) => {
  const [list, setList] = useState(null);
  const [sel, setSel] = useState(null);

  useEffect(() => {
    axios.get(`${API}/commerce/review/workspace`, { params: { sort: "decision_confidence", limit: 60 } })
      .then(({ data }) => { setList(data.items); setSel(initialCid || (data.items[0] && data.items[0].cluster_id)); })
      .catch(() => setList([]));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (initialCid) setSel(initialCid); }, [initialCid]);

  if (!list) return <div className="text-center py-16 text-zinc-400" data-testid="dd-list-loading">Loading…</div>;

  return (
    <div className="grid lg:grid-cols-[280px_1fr] gap-5" data-testid="commerce-founder-diligence">
      <div className="space-y-1.5 max-h-[70vh] overflow-y-auto pr-1" data-testid="dd-picker">
        {list.map((o) => (
          <button key={o.cluster_id} onClick={() => setSel(o.cluster_id)}
            className={`w-full text-left px-3 py-2 rounded-xl text-xs transition-all ${sel === o.cluster_id ? "bg-violet-600 text-white" : "bg-white/70 text-zinc-600 hover:bg-violet-50 border border-violet-100"}`}
            data-testid={`dd-pick-${o.cluster_id}`}>
            <div className="font-semibold truncate">{o.label || o.cluster_id}</div>
            <div className={`text-[10px] ${sel === o.cluster_id ? "text-violet-200" : "text-zinc-400"}`}>{o.market} · DC {o.decision_confidence} · ES {o.evidence_strength}</div>
          </button>
        ))}
      </div>
      <div>{sel && <Report cid={sel} />}</div>
    </div>
  );
};

export default FounderDiligence;
